import React, { useCallback, useRef } from 'react'
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api'
import { getPhotoPlannerMapDefaults } from '../../lib/photoPlannerConfig'

const ICON_SRC = process.env.PUBLIC_URL + '/favicon-32x32.png'
const LEAF_PNG_BASE64 =
  'iVBORw0KGgoAAAANSUhEUgAAACAAAAAgCAYAAABzenr0AAAAAXNSR0IArs4c6QAABAtJREFUWEfFl1toHGUUx39ndhNba42tWFG0aqWgoHhBRS2+KFQfbFKTmcS2iH0oVqQQDF5qZmJHM5tIaRErRQwGpCA2mUl6SX1oQWhFXxTFQouKFSRN1SYFtTU22ct8dmY3l93snVDP0z585//9vvM/35lvhSCesZehJTcgahFIFCGBry6gcRbRRkhEfmafPRquneeQUM+wdMAtoq2AP4BvQB3D1w4z0HlyPljSANgaRvJL4JEKRH9C+ISk/xGDXb9XkJe1NAMA6B2PIeqLuUIqAVJTZIM4ik+RqINrn6oUZAYgbcXXwIM5IglgGDiDYgXCTQU2CUB2E49v4+D2C+WCZAPo5kuI7C6YrNTnIMcQtQpYDZKdn04cBrUBNxZYWjKyBRrbbyCijQBakUwfnw+RZD9S0wbqaSAXJKhaK67zQSmCuScwrG+B+0slAr/gq2eRSB3iBxutzNM/23BjbxfTygNg7gRpKwMgWDIBaiMTNUMsSO64dFVfnFMNX7UyENtVSG8ugP5GIxIZKBMgWJYixWYGnV6azedR0gPUzspPklJPMBjLc8Pmegf1r93IFbVnKgBIQ6hUM173ILq1GmE/sHBGQ/3K35N3cWTHeK5uvi4G3Tpd5LoVYFPjJNXD7Os6gW42IAyARKYXK2w8563yAAwrsKCxwioEy49D9CFcO47RboLmzNL4i8n48twZkb8ChvUqsL0KAFC04znd2JfG+8lk4HswMzKhNuPGgh6ZjgIWmI8i8lVVAHCeiejtDNnnaOm4B18F13rKiiO4zpOlAQy7FpLngMVVQSg68Zw3w1zd6kNoDn8r/mXp6DX09ASDKoz8FQgTzf2INFQFgDrLkrGbw40aO1YRUTNjWfPvpq/rRGkAw3wOZE91AMFpU2vwug+Fh9TNU4isCLV8Vc9AbKgMAPsqSIyA1FUJ0YvrbApzDfN9kC0ZnU24Tm9pgLR/7yC8XiXAMK5zSwagBWRvxvQt9DvTX9zCPRCS20sh+SNwXVUQcbWcA7HTNNl3oCV/qLwCQUaT2YQWvBfzfvuLcynW4DmHMF5eiLpyHAk1GnCdg+VZMLWq2WxHSaziKihewXN2hnm6eR6RxWhyL32dxysDCO3oeAHUe8CCskGU2oUXa033gfVb2srxq3HfvVg5QGiHtRIhaMy1JV5NaX1hL/3OugxA8IUdxXXum32A4k1Y6Khrt95KTdQAHgceAK7NP9TkMG7nUxmAP1GyB68zXZFMVAeQC7Z+6xIS0WWg6sCPgJaCmn9gcgy3e4z0aL8Ifj1u12fzD1CqKQzrNuB7FkWv52N74vIDBK8kWI/nbMxlnR8LSlbAbEPJd3jO0f8HQLc68JzgdRT8yc2Ky1OBFutO+pypUZwF8B9orEgwBkGVIgAAAABJRU5ErkJggg=='
const HIGHLIGHT_ICON_SRC = `data:image/svg+xml;utf8,${encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" viewBox="0 0 32 32">
    <defs>
      <filter id="tint" color-interpolation-filters="sRGB">
        <feColorMatrix type="matrix" values="
          0 0 0 0 0
          0 0 0 0.83 0
          0 0 0 0.6 0
          0 0 0 1 0" />
        <feComponentTransfer>
          <feFuncA type="gamma" amplitude="1" exponent="0.35" offset="0" />
        </feComponentTransfer>
        <feDropShadow dx="0" dy="0" stdDeviation="1.2" flood-color="#0f172a" flood-opacity="0.8" />
      </filter>
    </defs>
    <image width="32" height="32" href="data:image/png;base64,${LEAF_PNG_BASE64}" filter="url(#tint)" />
  </svg>
`)}`
const DISPLAY_SIZE = 30
const HIGHLIGHT_SIZE = 40

const mapContainerStyle = {
  width: '100%',
  height: '500px'
}

const mapOptions = {
  mapId: process.env.REACT_APP_MAP_ID,
  disableDefaultUI: true,
  zoomControl: true
}

const LIBRARIES = ['places']

export default function PhotoSpotsMapView({
  spots = [],
  onBoundsChanged,
  onViewChanged,
  initialCenter,
  initialZoom,
  island = 'kauai',
  highlightedSpotId,
  onMarkerSelect
}) {
  const mapRef = useRef(null)
  const viewportWidth = typeof window === 'undefined' ? 1024 : window.innerWidth
  const defaultView = getPhotoPlannerMapDefaults(island, viewportWidth)

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: LIBRARIES,
    mapIds: [process.env.REACT_APP_MAP_ID]
  })

  const onMapLoad = useCallback((map) => {
    mapRef.current = map
  }, [])

  const handleIdle = () => {
    if (!mapRef.current) return

    if (onBoundsChanged) {
      const bounds = mapRef.current.getBounds()
      onBoundsChanged(bounds)
    }

    if (onViewChanged) {
      const center = mapRef.current.getCenter?.()
      const zoom = mapRef.current.getZoom?.()
      if (center && typeof zoom === 'number') {
        onViewChanged({
          center: { lat: center.lat(), lng: center.lng() },
          zoom
        })
      }
    }
  }

  if (loadError) return <div>Error loading map</div>
  if (!isLoaded) return <div>Loading map…</div>

  return (
    <>
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={initialCenter ?? defaultView.center}
        zoom={initialZoom ?? defaultView.zoom}
        options={mapOptions}
        onLoad={onMapLoad}
        onIdle={handleIdle}
      >
        {spots
          .filter((spot) =>
            Number.isFinite(Number(spot?.location?.lat))
            && Number.isFinite(Number(spot?.location?.lng))
          )
          .map((spot) => {
            const isHighlighted = highlightedSpotId && (spot._id === highlightedSpotId)
            const size = isHighlighted ? HIGHLIGHT_SIZE : DISPLAY_SIZE
            return (
              <Marker
                key={spot._id || spot.placeID || spot.slug}
                position={spot.location}
                title={spot.name}
                zIndex={isHighlighted ? 999 : undefined}
                icon={{
                  url: isHighlighted ? HIGHLIGHT_ICON_SRC : ICON_SRC,
                  scaledSize: new window.google.maps.Size(size, size),
                  anchor: new window.google.maps.Point(size / 2, size)
                }}
                onClick={() => {
                  if (typeof onMarkerSelect === 'function') onMarkerSelect(spot)
                }}
              />
            )
          })}
      </GoogleMap>
    </>
  )
}
